import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Users as UsersIcon, Building2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "@/components/ui/sheet";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { CUSTOMER_STATUSES, CUSTOMER_STATUS_STYLES } from "@/lib/constants";

const Input = (props) => (
  <input {...props}
    className={
      "w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm " +
      "focus:ring-1 focus:ring-[var(--theme-color)] focus:border-[var(--theme-color)] outline-none " + (props.className || "")
    } />
);
const Field = ({ label, children }) => (
  <div><label className="text-label block mb-1.5">{label}</label>{children}</div>
);

export default function Customers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 25 });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const canCreate = user?.role === "master_admin" || user?.role === "admin";
  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers", { params: { q, page: 1, page_size: 50 } });
      setData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [q]);
  useEffect(() => {
    if (canCreate) api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  }, [canCreate]);

  const open = (c) => {
    setEditing(c);
    setForm(c || { customer_type: "Company", name: "" });
    setDrawerOpen(true);
  };
  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/customers/${editing.id}`, form);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", form);
        toast.success("Customer created");
      }
      setDrawerOpen(false);
      fetch();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  return (
    <div className="p-6 sm:p-8 space-y-6" data-testid="customers-page">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-label">CRM</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold mt-1">Customers</h1>
          <p className="text-sm text-muted-foreground mt-1">{data.total} customers · converted from Won leads</p>
        </div>
        {canCreate && (
          <button onClick={() => open(null)} data-testid="new-customer-btn"
            className="text-white text-sm font-medium rounded-sm py-2 px-3.5 hover:opacity-90 flex items-center gap-1.5"
            style={{ backgroundColor: "var(--theme-color)" }}>
            <Plus className="w-4 h-4" /> New customer
          </button>
        )}
      </header>

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search customers…"
        data-testid="customers-search-input"
        className="max-w-md w-full bg-transparent border border-border rounded-sm px-3 py-1.5 text-sm focus:ring-1 focus:ring-[var(--theme-color)] focus:border-[var(--theme-color)] outline-none"
      />

      <section className="border border-border rounded-sm bg-background overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40">
              <tr className="text-left">
                <Th>ID</Th><Th>Name</Th><Th>Type</Th><Th>Company</Th>
                <Th>Contact</Th><Th>Status</Th><Th>Assigned</Th><Th>Won Date</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">Loading…</td></tr>}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={8} className="text-center py-12">
                  <Building2 className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <div className="text-sm text-muted-foreground">No customers yet. Convert a Won lead to create one.</div>
                </td></tr>
              )}
              {data.items.map((c) => (
                <tr key={c.id} className="border-t border-border surface-hover cursor-pointer"
                  data-testid={`customer-row-${c.id}`}
                  onClick={() => canCreate && open(c)}>
                  <Td><span className="mono text-xs text-muted-foreground">{c.customer_no}</span></Td>
                  <Td><span className="font-medium">{c.name}</span></Td>
                  <Td>{c.customer_type}</Td>
                  <Td>{c.company || "—"}</Td>
                  <Td className="text-xs">
                    {c.email && <div>{c.email}</div>}
                    {c.phone && <div className="text-muted-foreground">{c.phone}</div>}
                  </Td>
                  <Td>
                    <span data-testid={`status-${c.id}`} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-sm ${CUSTOMER_STATUS_STYLES[c.status || "Active"]}`}>
                      {c.status || "Active"}
                    </span>
                  </Td>
                  <Td className="text-xs">{userMap[c.assigned_executive_id]?.full_name || "—"}</Td>
                  <Td className="text-xs mono text-muted-foreground">
                    {c.won_date ? new Date(c.won_date).toLocaleDateString() : "—"}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editing ? "Edit customer" : "Create customer"}</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-4">
            <Field label="Name"><Input value={form.name || ""} onChange={set("name")}
              data-testid="customer-name-input" /></Field>
            <Field label="Type">
              <Select value={form.customer_type || "Company"} onValueChange={set("customer_type")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Company">Company</SelectItem>
                  <SelectItem value="Individual">Individual</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Company"><Input value={form.company || ""} onChange={set("company")} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email"><Input type="email" value={form.email || ""} onChange={set("email")} /></Field>
              <Field label="Phone"><Input value={form.phone || ""} onChange={set("phone")} /></Field>
            </div>
            <Field label="Address"><Input value={form.address || ""} onChange={set("address")} /></Field>
            <Field label="Status">
              <Select value={form.status || "Active"} onValueChange={set("status")}>
                <SelectTrigger data-testid="customer-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CUSTOMER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Assigned Executive">
              <Select value={form.assigned_executive_id || ""} onValueChange={set("assigned_executive_id")}>
                <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {users.filter((u) => u.is_active).map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Notes">
              <textarea value={form.notes || ""} onChange={set("notes")} rows={3}
                className="w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm" />
            </Field>
          </div>
          <SheetFooter className="mt-6">
            <button onClick={save} disabled={!form.name} data-testid="save-customer-btn"
              className="text-white text-sm font-medium rounded-sm py-2 px-4 hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: "var(--theme-color)" }}>
              {editing ? "Save" : "Create"}
            </button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Th({ children }) { return <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }
